import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllRegions.graphql';
import { Query, QueryallRegionsArgs } from '../../__generated__/globalTypes';

export const useAllRegionsQuery = (options?: QueryHookOptions<Query, QueryallRegionsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallRegionsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllRegionsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallRegionsArgs>
): QueryResult<Query, QueryallRegionsArgs> & { getAllRegions: LazyQueryExecFunction<Query, QueryallRegionsArgs> } => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllRegions, result] = useLazyQuery<Query, QueryallRegionsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllRegions, ...result };
};

export const useAllRegionsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllRegions = useCallback(
    (options?: Omit<QueryOptions<QueryallRegionsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallRegionsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllRegions };
};
// Suspense Query Hooks
export const useAllRegionsSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallRegionsArgs>) => {
  const result = useSuspenseQuery<Query, QueryallRegionsArgs>(QUERY, {
    ...options
  });

  return result;
};
